// components/SystemAdminsList.tsx

import React, { useState, useEffect } from "react";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Switch } from "@shared/ui/switch";
import { Users, Edit, Save, X, AlertCircle, Trash2,Send, Link2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useUsers,
  useUpdateUser,
  useDeleteUser,
  useUpdateUserName,
  toggleUser,
} from "../hooks";
import { UserInfo } from "../types";
import { PAGE_SIZE } from "@shared/utils/constants";
import { TableColumn } from "@shared/types/table";
import { GenericTable } from "@shared/components/GenericTable";
import { useResendInviteOrg } from "@features/onboarding/utility-setup/hooks";
import { toast } from "@shared/hooks/use-toast";

const SystemAdminsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsPerPage = PAGE_SIZE;

  // Get current page from URL
  const currentPage = parseInt(searchParams.get("page") || "1");

  const { data: usersData } = useUsers(currentPage, itemsPerPage);
  const resendInviteOrg = useResendInviteOrg();
  
  const updateUserMutation = useUpdateUserName();
  const toggleUserMutation = toggleUser();
  const deleteUserMutation = useDeleteUser();

  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [editedFullName, setEditedFullName] = useState<string>("");

  const users = usersData?.result || [];
  const totalUsers = usersData?.count || 0;
  const activeUsers = usersData?.activeUserCount || 0;
  const totalPages = Math.ceil(totalUsers / itemsPerPage);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const startEditing = (user: UserInfo) => {
    setEditingUser(user.id);
    setEditedFullName(user.user.name);
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setEditedFullName("");
  };

    // Handle resend invite
  const handleResendInvite = (email: string) => {
    resendInviteOrg.mutate({ email });
  };

  const handleCopyInviteLink = async (link?: string) => {
    if(link) {
      await navigator.clipboard.writeText(link);
      toast({title: "Invite link copied:", description: "Reset link copied to clipboard."});
    } 
    else {
      toast({title: "Failed to copy link", variant: "destructive"})
    }
  }


  const saveUserName = (userInfo: any) => {
    if (editedFullName.trim() === "") return;

    const payload = {
      id: userInfo.id,
      name: editedFullName,
      email: userInfo.email,
    };

    updateUserMutation.mutate(
      {
        userId: payload.id,
        payload,
      },
      {
        onSuccess: async () => {
          setEditingUser(null);
          setEditedFullName("");
        },
      }
    );
  };

  const toggleUserStatus = (userId: number, isActive: boolean) => {
    toggleUserMutation.mutate({
      userId,
      payload: { active: isActive, id: userId },
    });
  };

  const removeUser = (userInfo: any) => {
    const userID = userInfo.user.id;

    deleteUserMutation.mutate(userID, {
      onSuccess: async () => {
        setEditingUser(null);
        setEditedFullName("");

        // If we're on a page that would be empty after deletion, go to previous page
        const remainingUsers = totalUsers - 1;
        const maxPage = Math.ceil(remainingUsers / itemsPerPage);
        if (currentPage > maxPage && maxPage > 0) {
          handlePageChange(maxPage);
        }
      },
    });
  };


  const columns: TableColumn<UserInfo>[] = [
    {
      key: "user",
      header: "User",
      width: "w-[45%]",
      render: (value, row) => {
        const user = row.user;
        const isEditing = editingUser === row.id;

        if (isEditing) {
          return (
            <div className="flex items-center space-x-2 w-full">
              <Input
                value={editedFullName}
                onChange={(e) => setEditedFullName(e.target.value)}
                className="h-8"
                autoFocus
                disabled={updateUserMutation.isPending}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => saveUserName(row.user)}
                className="h-8 w-8 text-primary"
                title="Save"
                disabled={updateUserMutation.isPending}
              >
                {updateUserMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={cancelEditing}
                className="h-8 w-8 text-muted-foreground"
                title="Cancel"
                disabled={updateUserMutation.isPending}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          );
        }

        return (
          <div>
            <div className="font-medium truncate">
              {user.name || user.username}
            </div>
            <div className="text-sm text-gray-500 truncate">{user.email}</div>
          </div>
        );
      },
    },
    {
      key: "lastLogin",
      header: "Last Login",
      width: "w-[20%]",
      render: (value, row) => (
        <span className="whitespace-nowrap">
          {row.user.lastLogin
            ? row.user.lastLogin
            : "Never"}
        </span>
      ),
    },
    {
      key: "status",
      header: <div className="w-full text-center">Status</div>,
      width: "w-[15%]",
      type: "badge",
      render: (value, row) => (
        <div className="w-full flex justify-center">
          <Badge
            variant={row.user.isActive ? "default" : "secondary"}
            className="capitalize w-24 flex justify-center"
          >
            {row.status}
          </Badge>
        </div>
      ),
    },
    {
      key: "actions",
      header: <div className="w-full text-center">Actions</div>,
      width: "w-[20%]",
      render: (value, row) => {
        const isEditing = editingUser === row.id;

        return (
          <div className="w-full flex items-center justify-center gap-2">
            {!isEditing && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => startEditing(row)}
                disabled={
                  updateUserMutation.isPending || deleteUserMutation.isPending
                }
                title="Edit"
              >
                <Edit className="h-4 w-4" />
              </Button>
            )}

            <Switch
              checked={row.user.isActive}
              disabled={row.status === "EXPIRED"}
              onCheckedChange={(checked) =>
                toggleUserStatus(row.user.id, checked)
              }
            />

            <Button
              size="icon"
              variant="ghost"
              onClick={() => removeUser(row)}
              disabled={
                updateUserMutation.isPending || deleteUserMutation.isPending
              }
              title="Remove"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>

            {row.status === "EXPIRED" && (
              <button
                onClick={() => handleCopyInviteLink(row?.resetUrl)}
                className="text-blue-600 hover:text-blue-900 transition-colors"
                title="Copy Invite Link"
              >
                <Link2 className="h-4 w-4" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  // Define table actions
  const actions = [
    {
      label: "Edit",
      icon: <Edit className="h-4 w-4" />,
      onClick: (row: UserInfo) => startEditing(row),
      disabled: (row: UserInfo) =>
        updateUserMutation.isPending || deleteUserMutation.isPending,
      hidden: (row: UserInfo) => editingUser === row.id,
    },
    {
      label: "Toggle Status",
      onClick: (row: UserInfo) =>
        toggleUserStatus(row.user.id, !row.user.isActive),
      disabled: (row: UserInfo) => updateUserMutation.isPending,
      render: (row: UserInfo) => (
        <Switch
          checked={row.user.isActive}
          onCheckedChange={(checked) => toggleUserStatus(row.user.id, checked)}
          disabled={updateUserMutation.isPending}
        />
      ),
    },
    {
      label: "Remove User",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (row: UserInfo) => removeUser(row),
      disabled: (row: UserInfo) =>
        updateUserMutation.isPending || deleteUserMutation.isPending,
      variant: "destructive" as const,
    },
  ];

  // Prepare pagination data
 const pagination = usersData
    ? {
        currentPage,
        totalPages: Math.ceil(usersData.count / PAGE_SIZE),
        totalItems: usersData.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, usersData.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  return (
    <Card className="bg-white shadow-sm border">
      <CardContent className="">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <span className="inline-flex items-center justify-center bg-green-100 text-green-700 rounded-full p-2 mr-2">
              <Users className="h-5 w-5" />
            </span>
            <h3 className="text-lg font-medium">
              System Admins ({totalUsers})
            </h3>
          </div>
          {totalUsers > 0 && (
            <div className="flex gap-2">
              <Badge variant="outline">{activeUsers} Active</Badge>
              <Badge variant="secondary">
                {totalUsers - activeUsers} Inactive
              </Badge>
            </div>
          )}
        </div>

        <GenericTable<UserInfo>
          data={users}
          columns={columns}
          rowKey="id"
          pagination={pagination}
          onPageChange={handlePageChange}
          emptyMessage="No system admins added yet. Add system admins by filling the form above."
          emptyIcon={
            <AlertCircle className="h-8 w-8 text-muted-foreground mb-2" />
          }
          getRowClassName={(row) =>
            editingUser === row.id
              ? "bg-muted/30"
              : "hover:bg-muted/20 transition"
          }
        />
      </CardContent>
    </Card>
  );
};

export default SystemAdminsList;
